import type { CourseSessionDto } from "@/types/api/courseSessionDto";
import type { CourseSessionRequest } from "@/types/api/courseSessionRequest";
import { toNum } from "@/utils/course-formatters";

/*
 * Sessions of an instructor-led course (LMS-36). Admins enter and read times in UK time
 * (Europe/London) wherever the browser is. The API takes and returns UK wall-clock times such as
 * "2026-12-01T09:30" and stores them in UTC.
 */
export const UK_TIME_ZONE = "Europe/London";

export const MIN_SESSION_CAPACITY = 1;
export const MAX_SESSION_CAPACITY = 10_000;
export const VENUE_NAME_MAX_LENGTH = 200;
export const VENUE_ADDRESS_MAX_LENGTH = 500;

const ukDateFormat = new Intl.DateTimeFormat("en-GB", {
  weekday: "short",
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: UK_TIME_ZONE,
});

const ukTimeFormat = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  timeZone: UK_TIME_ZONE,
});

const ukWallClockFormat = new Intl.DateTimeFormat("en-GB", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  timeZone: UK_TIME_ZONE,
});

const partsOf = (format: Intl.DateTimeFormat, date: Date) =>
  Object.fromEntries(format.formatToParts(date).map((part) => [part.type, part.value]));

/** "Tue 1 Dec 2026" in UK time. */
export const formatUkDate = (instant: string | Date) => {
  const parts = partsOf(ukDateFormat, new Date(instant));
  return `${parts.weekday} ${parts.day} ${parts.month} ${parts.year}`;
};

/** "09:30" in UK time. */
export const formatUkTime = (instant: string | Date) => {
  const parts = partsOf(ukTimeFormat, new Date(instant));
  return `${parts.hour}:${parts.minute}`;
};

/** The UK wall-clock time of an instant, as the API takes it: "2026-12-01T09:30". */
export const toUkWallClock = (instant: Date) => {
  const parts = partsOf(ukWallClockFormat, instant);
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

const WALL_CLOCK_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
const MINUTE_MS = 60_000;

/**
 * The instant a UK wall-clock time ("2026-12-01T09:30") stands for, read the way the API reads it
 * (UkTime.TryParseToUtc): a time that happens twice when the clocks go back is the later one (GMT),
 * and a time skipped when they go forward doesn't exist, so it gives undefined.
 */
export const ukWallClockToInstant = (wallClock: string): Date | undefined => {
  const match = WALL_CLOCK_PATTERN.exec(wallClock);
  if (!match) return undefined;
  const [, year, month, day, hour, minute] = match.map(Number);
  const asIfUtc = Date.UTC(year, month - 1, day, hour, minute);
  // The UK is on GMT (UTC+0) or BST (UTC+1). Try GMT first so a repeated time reads as GMT.
  for (const offsetMinutes of [0, 60]) {
    const candidate = new Date(asIfUtc - offsetMinutes * MINUTE_MS);
    if (toUkWallClock(candidate) === wallClock) return candidate;
  }
  return undefined;
};

/** "Tue 1 Dec 2026", or "Tue 1 Dec 2026 to Thu 3 Dec 2026" for a session over several days. */
export const formatSessionDates = (session: Pick<CourseSessionDto, "startsAt" | "endsAt">) => {
  const start = formatUkDate(session.startsAt);
  const end = formatUkDate(session.endsAt);
  return start === end ? start : `${start} to ${end}`;
};

/** "09:30 to 16:30" in UK time. */
export const formatSessionTimes = (session: Pick<CourseSessionDto, "startsAt" | "endsAt">) =>
  `${formatUkTime(session.startsAt)} to ${formatUkTime(session.endsAt)}`;

/** "12 places", "1 place". */
export const formatCapacity = (capacity: number | string) => {
  const places = toNum(capacity);
  return `${places.toLocaleString("en-GB")} ${places === 1 ? "place" : "places"}`;
};

/**
 * How many booked learners cancelling the session tells (LMS-185): its bookings are cancelled and
 * each learner emailed, unless the session is over, when its bookings are kept.
 */
export const learnersToTellOnCancel = (
  session: Pick<CourseSessionDto, "hasEnded" | "learnersBooked">,
) => (session.hasEnded ? 0 : toNum(session.learnersBooked));

/** "1 learner is booked", "2 learners are booked". */
export const formatLearnersBooked = (learners: number) =>
  `${learners.toLocaleString("en-GB")} ${learners === 1 ? "learner is" : "learners are"} booked`;

/** "the booked learner is being emailed", "the 2 booked learners are being emailed". */
export const formatLearnersEmailed = (learners: number) =>
  learners === 1
    ? "the booked learner is being emailed"
    : `the ${learners.toLocaleString("en-GB")} booked learners are being emailed`;

/**
 * How many learners paid by card through Stripe, or are paying at checkout (LMS-186): the admin
 * chooses whether to refund them when cancelling. None once the session is over, as its bookings are
 * kept.
 */
export const learnersPaidOnCancel = (
  session: Pick<CourseSessionDto, "hasEnded" | "learnersPaid">,
) => (session.hasEnded ? 0 : toNum(session.learnersPaid ?? 0));

/**
 * "1 of them paid by card", "2 of them paid by card", "They all paid by card". Seats still being
 * paid for at checkout count too: "1 learner is paying by card" when nobody is booked yet, "3
 * learners paid or are paying by card" when some are still paying.
 */
export const formatLearnersPaid = (paid: number, booked: number) => {
  const count = paid.toLocaleString("en-GB");
  if (booked === 0)
    return paid === 1 ? "1 learner is paying by card" : `${count} learners are paying by card`;
  if (paid > booked) return `${count} learners paid or are paying by card`;
  if (paid === booked) return booked === 1 ? "They paid by card" : "They all paid by card";
  return `${count} of them paid by card`;
};

/**
 * How many purchasers' seat orders cancelling the session cancels (LMS-192): each purchaser is
 * emailed, and invites to seats nobody has taken up stop working. None once the session is over, as
 * it keeps its orders, as it keeps its bookings. 0 while seat orders are hidden by their flags.
 */
export const seatOrdersToTellOnCancel = (
  session: Pick<CourseSessionDto, "hasEnded" | "seatOrders">,
) => (session.hasEnded ? 0 : toNum(session.seatOrders ?? 0));

/**
 * How many of the session's seat orders were paid by card, or are being paid for at checkout
 * (LMS-192): the admin's refund choice applies to them too, each refunded whole. None once the
 * session is over.
 */
export const seatOrdersPaidOnCancel = (
  session: Pick<CourseSessionDto, "hasEnded" | "seatOrdersPaid">,
) => (session.hasEnded ? 0 : toNum(session.seatOrdersPaid ?? 0));

/** "1 seat order was bought on it", "2 seat orders were bought on it". */
export const formatSeatOrders = (orders: number) =>
  orders === 1
    ? "1 seat order was bought on it"
    : `${orders.toLocaleString("en-GB")} seat orders were bought on it`;

/**
 * "It was paid for by card", "They were all paid for by card", "1 of them was paid for by card".
 * Orders still being paid for at checkout count too: "1 seat order is being paid for by card" when
 * none has been bought yet, "3 seat orders were paid or are being paid for by card" when some are
 * still being paid for.
 */
export const formatSeatOrdersPaid = (paid: number, orders: number) => {
  const count = paid.toLocaleString("en-GB");
  if (orders === 0)
    return paid === 1
      ? "1 seat order is being paid for by card"
      : `${count} seat orders are being paid for by card`;
  if (paid > orders) return `${count} seat orders were paid or are being paid for by card`;
  if (paid === orders)
    return orders === 1 ? "It was paid for by card" : "They were all paid for by card";
  return `${count} of them ${paid === 1 ? "was" : "were"} paid for by card`;
};

/**
 * "the seat order's purchaser is being emailed", "the purchasers of the 2 seat orders are being
 * emailed".
 */
export const formatPurchasersEmailed = (orders: number) =>
  orders === 1
    ? "the seat order's purchaser is being emailed"
    : `the purchasers of the ${orders.toLocaleString("en-GB")} seat orders are being emailed`;

/** "1 refund was made", "2 refunds were made". */
export const formatRefundsIssued = (refunds: number) =>
  `${refunds.toLocaleString("en-GB")} ${refunds === 1 ? "refund was" : "refunds were"} made`;

/** "1 refund hasn't been made", "2 refunds haven't been made". */
export const formatRefundsOutstanding = (refunds: number) =>
  refunds === 1
    ? "1 refund hasn't been made"
    : `${refunds.toLocaleString("en-GB")} refunds haven't been made`;

/** "1 refund didn't go through", "2 refunds didn't go through". */
export const formatRefundsFailed = (failed: number) =>
  failed === 1
    ? "1 refund didn't go through"
    : `${failed.toLocaleString("en-GB")} refunds didn't go through`;

/** A session's details as typed in the form. Dates are "yyyy-mm-dd" and times "hh:mm", UK time. */
export interface SessionFormValues {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  venueName: string;
  venueAddress: string;
  capacity: string;
}

export type SessionFormField = "start" | "end" | "venueName" | "venueAddress" | "capacity";

export type SessionFormErrors = Partial<Record<SessionFormField, string>>;

export const emptySessionForm: SessionFormValues = {
  startDate: "",
  startTime: "",
  endDate: "",
  endTime: "",
  venueName: "",
  venueAddress: "",
  capacity: "",
};

/** The form filled in with a session's details, for editing it. */
export const sessionToFormValues = (session: CourseSessionDto): SessionFormValues => {
  const [startDate = "", startTime = ""] = session.startsAtUk.split("T");
  const [endDate = "", endTime = ""] = session.endsAtUk.split("T");
  return {
    startDate,
    startTime,
    endDate,
    endTime,
    venueName: session.venueName,
    venueAddress: session.venueAddress,
    capacity: String(toNum(session.capacity)),
  };
};

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const TIME_PATTERN = /^\d{2}:\d{2}$/;

const wallClock = (date: string, time: string) =>
  DATE_PATTERN.test(date.trim()) && TIME_PATTERN.test(time.trim())
    ? `${date.trim()}T${time.trim()}`
    : undefined;

const CLOCKS_GO_FORWARD =
  "That time doesn't exist in the UK because the clocks go forward then. Choose another time.";

/**
 * Why the session can't be saved, by field: an empty object when it can. The same rules as the
 * API, comparing the instants the UK times stand for: both times exist in the UK, it ends after it
 * starts, and it takes at least 1 learner. It can't start in the past, except that an edit which
 * keeps the session's start (`currentStartUk`) can correct one that has already started. `now` is
 * for tests.
 */
export const validateSessionForm = (
  values: SessionFormValues,
  now: Date = new Date(),
  currentStartUk?: string,
): SessionFormErrors => {
  const errors: SessionFormErrors = {};
  const start = wallClock(values.startDate, values.startTime);
  const end = wallClock(values.endDate, values.endTime);
  const startsAt = start ? ukWallClockToInstant(start) : undefined;
  const endsAt = end ? ukWallClockToInstant(end) : undefined;

  if (!start) errors.start = "Enter the date and time the session starts.";
  else if (!startsAt) errors.start = CLOCKS_GO_FORWARD;
  else if (start !== currentStartUk && startsAt < now)
    errors.start = "A session can't start in the past.";

  if (!end) errors.end = "Enter the date and time the session ends.";
  else if (!endsAt) errors.end = CLOCKS_GO_FORWARD;
  else if (startsAt && endsAt <= startsAt) errors.end = "The session must end after it starts.";

  const venueName = values.venueName.trim();
  if (!venueName) errors.venueName = "Enter the venue name.";
  else if (venueName.length > VENUE_NAME_MAX_LENGTH)
    errors.venueName = `The venue name can be at most ${VENUE_NAME_MAX_LENGTH} characters.`;

  const venueAddress = values.venueAddress.trim();
  if (!venueAddress) errors.venueAddress = "Enter the venue address.";
  else if (venueAddress.length > VENUE_ADDRESS_MAX_LENGTH)
    errors.venueAddress = `The venue address can be at most ${VENUE_ADDRESS_MAX_LENGTH} characters.`;

  const capacity = values.capacity.trim();
  if (!/^-?\d+$/.test(capacity) || Number(capacity) < MIN_SESSION_CAPACITY)
    errors.capacity = "Capacity must be at least 1.";
  else if (Number(capacity) > MAX_SESSION_CAPACITY)
    errors.capacity = `Capacity can be at most ${MAX_SESSION_CAPACITY.toLocaleString("en-GB")}.`;

  return errors;
};

/** The request for a valid form. */
export const toSessionRequest = (values: SessionFormValues): CourseSessionRequest => ({
  startsAtUk: `${values.startDate.trim()}T${values.startTime.trim()}`,
  endsAtUk: `${values.endDate.trim()}T${values.endTime.trim()}`,
  venueName: values.venueName.trim(),
  venueAddress: values.venueAddress.trim(),
  capacity: Number(values.capacity.trim()),
});

const API_FIELDS: Record<string, SessionFormField> = {
  startsatuk: "start",
  endsatuk: "end",
  venuename: "venueName",
  venueaddress: "venueAddress",
  capacity: "capacity",
};

/** The API's validation errors, by form field. */
export const toSessionFormErrors = (
  fieldErrors: Record<string, string[]> | undefined,
): SessionFormErrors =>
  Object.entries(fieldErrors ?? {}).reduce<SessionFormErrors>((errors, [key, messages]) => {
    const field = API_FIELDS[key.toLowerCase()];
    if (field && messages[0]) errors[field] = messages[0];
    return errors;
  }, {});
